/**
 * BridgeWalletContext
 * Combines Casper and Ethereum wallet state with chain selection for bridging.
 *
 * The hook (useBridgeWallet) is in useBridgeWallet.ts to satisfy
 * react-refresh/only-export-components — a file with a provider component
 * must not also export hooks.
 */

import { type ReactNode } from 'react';

import { useWallet } from 'hooks/useWallet';
import { useEthereumWallet } from './useEthereumWallet';
import { useChainContext } from './useChain';

import { BridgeWalletContext } from './bridgeWalletContextDef';
import type { BridgeWalletContextType } from './bridgeWalletContextDef';

export type { BridgeWalletContextType, NetworkType } from './bridgeWalletContextDef';

interface BridgeWalletProviderProps {
  children: ReactNode;
}

export const BridgeWalletProvider = ({ children }: BridgeWalletProviderProps) => {
  // Casper wallet from CSPR.click
  const { activeAccount, signIn } = useWallet();

  // Ethereum wallet from Wagmi + ConnectKit
  const {
    address: ethereumAddress,
    isConnected: ethereumConnected,
    openConnectModal: ethereumOpenConnectModal,
    disconnect: ethereumDisconnect,
    isWrongNetwork,
    switchToSelectedChain,
  } = useEthereumWallet();

  // Chain selection from ChainContext
  const {
    selectedChain,
    selectedChainId,
    setSelectedChain,
    getLockerAddress,
    getExplorerTxUrl,
  } = useChainContext();

  const casperConnected = !!activeAccount;
  const bothWalletsConnected = casperConnected && ethereumConnected;

  const getAddressForNetwork = (network: 'ethereum' | 'casper'): string | undefined => {
    if (network === 'casper') {
      return activeAccount?.public_key;
    }

    return ethereumAddress;
  };

  const value: BridgeWalletContextType = {
    // Casper
    casperAccount: activeAccount,
    casperConnected,
    casperSignIn: signIn,

    // Ethereum
    ethereumAddress,
    ethereumConnected,
    ethereumOpenConnectModal,
    ethereumDisconnect,

    // Selected chain
    selectedChain,
    selectedChainId,
    selectedChainName: selectedChain?.name,
    setSelectedChain,

    // Combined
    bothWalletsConnected,
    isWrongNetwork,
    switchToSelectedChain,
    getAddressForNetwork,

    // Chain-specific helpers
    getLockerAddress,
    getExplorerTxUrl,
  };

  return (
    <BridgeWalletContext.Provider value={value}>
      {children}
    </BridgeWalletContext.Provider>
  );
};
